"use client";

import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";

import Doodle, { DOODLE_LIGHT } from "@/components/doodles";

/* A pen frame drawn around the 4:5 portrait: an imperfect rounded rectangle
   that overshoots its own start, like the loops around the faces in the home
   hero, plus a leaf and a heart on the photo's corners (the chat sticker lives on
   the intro-session button just below, so it is not repeated here). Sits in a `relative`
   wrapper around the photo and never takes up space of its own.

   The SVG is inset by the same percentage on every side, so it keeps the
   photo's 4:5 ratio and the stroke is never stretched. */
const FRAME =
  "M42 9 C 150 5, 280 11, 368 8 C 390 8, 394 24, 393 58 C 390 200, 396 360, 392 466 C 391 486, 381 492, 354 491 C 250 488, 140 494, 42 490 C 18 490, 8 482, 9 456 C 11 340, 6 180, 8 46 C 8 20, 19 8, 70 13";

const PAGE_GROUND = { ...DOODLE_LIGHT, ground: "#FFF9F5" };

export default function PhotoPenFrame() {
  const ref = useRef<SVGSVGElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const still = useReducedMotion() ?? false;
  const play = inView || still;

  return (
    <>
      <svg
        ref={ref}
        aria-hidden
        viewBox="0 0 400 500"
        className="pointer-events-none absolute -inset-[4%] size-[108%] overflow-visible"
      >
        <motion.path
          d={FRAME}
          fill="none"
          stroke="#2C969B"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={still ? false : { pathLength: 0, opacity: 0 }}
          animate={play ? { pathLength: 1, opacity: 1 } : undefined}
          transition={{
            pathLength: { duration: still ? 0 : 1.1, delay: still ? 0 : 0.2, ease: [0.45, 0, 0.2, 1] },
            opacity: { duration: 0.01, delay: still ? 0 : 0.2 },
          }}
        />
      </svg>
      <Doodle
        name="leaf"
        palette={PAGE_GROUND}
        play={play}
        delay={1.2}
        className="absolute -right-2.5 -top-3.5 size-8 sm:-right-4 sm:-top-4 sm:size-9"
      />
      <Doodle
        name="heart"
        palette={PAGE_GROUND}
        play={play}
        delay={1.5}
        className="absolute -bottom-3 -left-3 size-7 sm:-bottom-4 sm:-left-4 sm:size-8"
      />
    </>
  );
}
